import type { OTIFGradeBand, PPMGradeBand } from './SiteKPIComponents'
import type { ArrearsValueSource } from './arrearsSnapshot'
import type { ArrearsBacklogBar } from './arrearsBacklogDrill'
import type { ChartMilestone } from '../../lib/api/milestones'
import type { PriceChangeRequestsSeries } from '../../lib/api/priceChangeRequests'
import type { DeliveryMetricMode } from '../../lib/api/types/delivery-metric-mode'

// Recharts chart click event data (from onClick handlers on chart components)
export interface RechartsClickData {
  activePayload?: Array<{
    payload?: Record<string, unknown>
    dataKey?: string
    value?: number
    name?: string
    [key: string]: unknown
  }>
  activeLabel?: string
  [key: string]: unknown
}

// CR-5456: PPV (Purchase Price Variance) dashboard chart data.
// One PPVViewData per view (Future = outstanding qty by promise date,
// Historical = received qty by required date). Variance lists are pre-sorted
// top-20 (positive = cost overrun desc, negative = savings asc); the Top-N
// selector slices client-side.
export interface PPVOverTimePoint {
  month: string
  ppv: number
  ppv_pct: number
  arrears?: number // DORMANT — not sent since CR-1421 decision 3 (the Future view follows the period)
}

export interface PPVVarianceItem {
  name: string
  variance: number
}

export interface PPVViewData {
  over_time: PPVOverTimePoint[]
  by_supplier_positive: PPVVarianceItem[]
  by_supplier_negative: PPVVarianceItem[]
  by_part_positive: PPVVarianceItem[]
  by_part_negative: PPVVarianceItem[]
  /** PPV-POLISH: decimals the server listed the four panels' rows at — their labels print at it. */
  label_decimals?: number
}

export interface PPVDashboardData {
  future: PPVViewData
  historical: PPVViewData
}

export interface SupplierRanking {
  supplier_id: number
  supplier_name: string
  /** CR-8609: null = no rate (rejects against nothing received): shown N/A */
  value: number | null
  /** null = no target could be read (CR-7714 FE-20) */
  target?: number | null
  /** null = no last-month figure (METRIC-DEFINITIONS Q13): no comparison, no arrow */
  trend?: 'up' | 'down' | 'stable' | null
  percentage_change?: number | null
}

export interface MetricDelta {
  current: number | null
  /** null = last month had nothing to score (METRIC-DEFINITIONS Q13): no comparison */
  previous: number | null
  change: number | null
  percentage_change: number | null
  trend: 'up' | 'down' | 'stable'
}

export interface DashboardData {
  // Company overview
  companies: {
    total: number
    customers: number
    suppliers: number
  }
  // Sites overview
  sites: {
    total: number
  }
  // Users overview
  users: {
    total: number
    pendingApprovals: number
  }
  // Purchasing metrics
  purchasing: {
    totalPOs: number
    openPOs: number
    totalLines: number
    byStatus: Record<string, number>
    totalValue: number
  }
  // Quality metrics
  // CR-4641 round 2: ppm and otif are nullable when the SupplierProfile
  // include flag excludes all matching suppliers (backend returns null;
  // frontend hides the chart card and disables drill-down).
  quality: {
    ppm: number | null
    /** null = no target configured (CR-7714 FE-20) */
    ppmTarget: number | null
    ncrs: number
    ncrsByStatus: Record<string, number>
    ncrsBySeverity: Record<string, number>
    otif: number | null
    /** null = no target configured (CR-7714 FE-20) */
    otifTarget: number | null
  }
  // Delivery metrics
  delivery: {
    otd: number | null
    /** null = no target configured (CR-7714 FE-20) */
    otdTarget: number | null
    appeals: number
    appealsBreakdown?: {
      raised_by_customer: number
      raised_by_supplier: number
      accepted: number
      rejected: number
      auto_approved: number
      // CR-7167: Action required counts for dashboard
      action_required_customer?: number
      action_required_supplier?: number
    }
    openOrderValue: number
  }
  // Supplier metrics
  suppliers: {
    total: number
    active: number
    preferred: number
    avgQuality: number
    avgDelivery: number
  }
  // Cost metrics
  cost: {
    totalSpend: number
    historicalArrears: number
    /** 'none' = the period's month-end arrears were never captured: show "No snapshot", not £0 */
    historicalArrearsSource?: ArrearsValueSource
  }
  // NEW: Supplier rankings for actionable insights
  supplierRankings?: {
    worstPPM: SupplierRanking[]
    worstOTIF: SupplierRanking[]
    worstOTD: SupplierRanking[]
    highestArrears: SupplierRanking[]
  }
  // NEW: Month-over-month deltas for KPIs (Phase 5). null = the KPI has no
  // delta (not scored, or switched off); CR-8867 carries these through.
  deltas?: {
    ppm: MetricDelta | null
    otif: MetricDelta | null
    otd: MetricDelta | null
    ncrs: MetricDelta
    open_order_value: MetricDelta
    /** CR-8867: this month's spend against last month's */
    total_spend: MetricDelta | null
    arrears_value: MetricDelta
  }
  // CR-4211: Configurable targets from SupplierPerformanceSettings
  // Extended with three-tier color thresholds
  targets?: {
    // null = no target could be read (CR-7714 FE-20, `targets_unavailable`)
    ppm: number | null
    otif: number | null
    otd: number | null
    // Three-tier color thresholds for KPI cards (green/yellow/red)
    // CR-6267: configurable OTIF colour bands (replaces otif_excellent/good/poor)
    otif_grade_bands?: OTIFGradeBand[]
    // CR-6652: configurable PPM colour bands (replaces ppm_excellent/good/poor)
    ppm_grade_bands?: PPMGradeBand[]
    ppm_excellent?: number
    ppm_good?: number
    ppm_poor?: number
  }
  // Delivery metric mode: which metrics are active (CR-8697: incl. 'none')
  delivery_metric_mode?: DeliveryMetricMode
  /** CR-7714 BL-14: the currency the money in this payload is in (the response's `currency`). */
  values_currency?: string | null
  /** CR-7714 BL-14: the currency asked for when no rate into it exists; the money is then in `values_currency`. */
  currency_conversion_unavailable?: string | null
  /** CR-7714 BL-14: reporting currencies left out of the totals on a mixed scope (no rate). */
  currency_excluded_currencies?: string[] | null
  /** CR-7714 FE-20: no settings row could be read; nothing is judged against a target. */
  targets_unavailable?: boolean
  // CR-1421: the params the Cost data was fetched with, so the weekly charts'
  // own endpoint is scoped exactly as the rest of the tab.
  costParams?: Record<string, string | number | boolean>
  costData?: {
    /** CR-863: Cost › Historical supplier price change requests; null on a schedule-line or mixed scope. */
    price_change_requests?: PriceChangeRequestsSeries | null
    /** CR-8627: the customer's ERP price changes, same shape; null on a schedule-line or mixed scope or the supplier portal. */
    customer_price_changes?: PriceChangeRequestsSeries | null
    total_incoming_value: number
    total_incoming_lines: number
    total_arrears_value: number
    total_arrears_lines: number
    total_suppliers: number
    incoming_by_week: Array<{
      week: string
      value: number
      // CR-5455 rework: bucket bounds (ISO dates) so the chart's click
      // handler can scope the drill-down to the exact week clicked.
      week_start?: string
      week_end?: string
    }>
    incoming_by_month?: Array<{
      month: string
      value: number
      lines: number
    }>
    arrears_by_supplier: Array<{
      supplier: string
      arrears: number
      count: number
    }>
    // CR-7714 BL-16: line rows are in the PO's own `currency`; null value = unpriced.
    incoming_lines?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty: number
      unit_price: number | null
      total_value: number | null
      currency?: string | null
      delivery_date: string
    }>
    arrears_lines?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty_outstanding: number
      days_overdue: number
      value: number | null
      currency?: string | null
      due_date: string
    }>
    // CR-5456: PPV (Purchase Price Variance) chart data. Only present for
    // customer/helix users — the backend gates it out for supplier-type users.
    ppv?: PPVDashboardData
  }
  siteKPI: {
    otifBySite: Array<{
      site: string
      /** wave 3a: the site's Company id, so a Group Detail bar click can scope its drill */
      customer_id?: number
      /** null = N/A (nothing closed or overdue in the window) */
      otif: number | null
      target: number
      orders: number
      /** CR-6543: backend counts (CR-6155 buckets) so the UI never derives them */
      total_lines?: number
      otif_lines?: number
      arrears_lines?: number
    }>
    // CR-7714 / CR-8698: the backend (company_kpi.ppm_by_company) sends
    // company, ppm and the parts rejected / received behind it; site and
    // target are not sent.
    ppmBySite: Array<{
      company: string
      /** null when nothing was received: the rate is undefined, not 0 */
      ppm: number | null
      rejected: number
      received: number
      site?: string
      target?: number
    }>
    ncrBreakdown: Array<{
      month: string
      open: number
      closed: number
      overdue: number
    }>
    deliveryBySite: Array<{
      site: string
      /** CR-9069: the site's Company id — scopes the OTD bar's drill. */
      customer_id?: number
      onTime: number
      early: number
      late: number
      /** CR-6543: every GRN in scope — the OTD ring's denominator */
      total?: number
    }>
  }
  groupKpi?: {
    internal: {
      month_otif: number | null
      fytd_otif: number | null
      month_otd: number | null
      fytd_otd: number | null
      month_ppm: number
      fytd_ppm: number
      otif_trend: Array<{
        month: string
        otif: number
        target: number
      }>
      otd_trend: Array<{
        month: string
        otd: number | null
        target: number
      }>
      ppm_trend: Array<{
        month: string
        ppm: number
        target: number
      }>
      total_spend: number
      components_delivered: number
      components_rejected: number
      arrears_value: number
      /** 'none' = the period's month-end arrears were never captured */
      arrears_value_source?: ArrearsValueSource
      arrears_count: number
    } | null
    external: {
      month_otif: number | null
      fytd_otif: number | null
      month_otd: number | null
      fytd_otd: number | null
      month_ppm: number
      fytd_ppm: number
      otif_trend: Array<{
        month: string
        otif: number
        target: number
      }>
      otd_trend: Array<{
        month: string
        otd: number | null
        target: number
      }>
      ppm_trend: Array<{
        month: string
        ppm: number
        target: number
      }>
      total_spend: number
      components_delivered: number
      components_rejected: number
      arrears_value: number
      /** 'none' = the period's month-end arrears were never captured */
      arrears_value_source?: ArrearsValueSource
      arrears_count: number
    } | null
  }
  charts: {
    ppmTrend: Array<{
      month: string
      ppm: number
      target: number
    }>
    otifData: Array<{
      month: string
      /** null = no data in the bar (METRIC-DEFINITIONS Q13) */
      otif: number | null
      target: number
      otifCount: number | null
      arrears: number | null
      failed: number | null
      /** the denominator the bar's % used: closed judged lines + arrears */
      denominator?: number | null
      /** where the arrears term came from: live stock, that month's frozen snapshot, or none captured */
      arrearsSource?: 'live' | 'snapshot' | 'none' | null
    }>
    spendByMonth: Array<{
      month: string
      spend: number
      lines?: number
    }>
    otdData: Array<{
      month: string
      otd: number
      target: number
      onTime: number
      early: number
      late: number
    }>
    liveNCRsOverdue: Array<{
      month: string
      overdue: number
      pending: number
    }>
    /** W3-T14: frozen month-end snapshot value/count per month (Historical only; an uncaptured month is null).
     *  CR-8660: an uncaptured month between two captured ones is a zero balance, source 'empty'. */
    historicalArrears: Array<{
      month: string
      value: number | null
      count?: number | null
      source?: ArrearsValueSource
    }>
    /** W3-T14: today's open-overdue lines by the month/week they were due (the former "Historical Arrears" series). */
    openArrearsByDueMonth?: Array<{
      month: string
      value: number
      count?: number
    }>
    /** CR-8236: each bar carries its own inclusive due-date window. */
    arrearsBacklog?: ArrearsBacklogBar[]
    /** CR-5880: active milestones whose period is on the chart. */
    milestones?: ChartMilestone[]
    dateAdherenceData?: Array<{
      supplier: string
      month: string
      total: number
      onTime: number
      adherence: number
    }>
    appealsTrend?: Array<{
      month: string
      awaitingCustomer: number
      accepted: number
      awaitingSupplier: number
      rejected: number
    }>
    ppmNCRs?: Array<{
      ncr_id: string
      customer_ncr_number: string
      customer: string
      supplier: string
      part_number: string
      qty_rejected: number
      reject_value: number
      date_raised: string
      liability: string
      status: string
      time_taken: number
    }>
    ppmGRNs?: Array<{
      grn_number: string
      customer: string
      supplier: string
      po_number: string
      part_number: string
      qty: number
      delivery_date: string
    }>
    otifLines?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty: number
      delivery_date: string
      otif_status: string
    }>
    otifPOL?: Array<{
      po_number: string
      po_line: string
      supplier: string
      part_number: string
      qty_ordered: number
      qty_outstanding: number
      delivery_date: string
    }>
    otifArrears?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty_outstanding: number
      days_overdue: number
      /** CR-7714 BL-16: in the PO's own `currency`; null = unpriced. */
      value: number | null
      currency?: string | null
    }>
    otifFailed?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_late: number
    }>
    otdOntime?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_diff: number
      status: string
    }>
    otdEarly?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_diff: number
      status: string
    }>
    otdLate?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_diff: number
      status: string
    }>
    otdAll?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty: number
      planned_date: string
      actual_date: string
      days_diff: number
      status: string
    }>
    // CR-7714 BL-16: line rows are in the PO's own `currency`; null value = unpriced.
    spendLines?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty: number
      unit_price: number | null
      total_value: number | null
      currency?: string | null
      order_date: string
    }>
    arrearsDetails?: Array<{
      po_number: string
      supplier: string
      part_number: string
      qty_outstanding: number
      days_overdue: number
      value: number | null
      currency?: string | null
      due_date: string
    }>
    ncrDetails?: Array<{
      ncr_id: string
      customer_ncr_number: string
      supplier: string
      part_number: string
      qty_rejected: number
      date_raised: string
      due_date: string
      days_overdue: number
      status: string
    }>
  }
}

export interface LoadingStates {
  core: boolean
  purchasing: boolean
  quality: boolean
  suppliers: boolean
}

export interface DashboardFilters {
  dateFrom: string
  dateTo: string
  supplier: string
  customer: string
  site: string
  currency: string
}

export interface TabProps {
  data: DashboardData
  currency: {
    currentCurrency: string
  }
  filters?: DashboardFilterState
  onDrillDown?: (type: string, data?: Record<string, unknown>) => void
  loadingStates?: LoadingStates
  // CR-3930: Weekly/Monthly toggle
  dataPeriod?: 'weekly' | 'monthly'
  // CR-3931: Orders toggle
  orderType?: 'all' | 'scheduled' | 'spot'
}

// ============================================================================
// Saved Filter Types
// ============================================================================

export interface SavedDashboardFilter {
  id: number
  name: string
  description?: string
  date_from?: string | null
  date_to?: string | null
  supplier?: number | null
  supplier_name?: string
  customer?: number | null
  customer_name?: string
  site?: number | null
  site_name?: string
  currency: string
  supplier_group?: number | null
  supplier_group_name?: string
  strategic_only?: boolean
  supplier_type?: string
  commodity_group?: number | null
  commodity_type?: number | null
  is_shared: boolean
  is_default: boolean
  created_by: number
  created_by_name?: string
  created_at: string
  updated_at?: string
}

// ============================================================================
// Extended Filter State (includes all filter options)
// ============================================================================

export interface DashboardFilterState {
  dateFrom: string
  dateTo: string
  supplier: string
  customer: string
  site: string
  currency: string
  supplierGroup: string
  strategicOnly: boolean
  supplierType: string
  commodityGroup: string
  commodityType: string
  // CR-3932 to CR-3937: Additional filters
  siteCountry: string
  supplierCountry: string
  supplierCode: string
  orderType: string
  warehouse: string
  dataMode: 'historical' | 'live'
  // CR-4349: Division filter (conditional on org structure)
  division: string
  // CR-4800: Compliance-page-only filters
  approvalStage: string
  riskCategory: string
  /**
   * Measure call-off suppliers from their warehouse-booking date instead of
   * the GRN date, for both OTIF and historical arrears. Non-call-off
   * suppliers are unaffected and always stay on GRN. A basis toggle for the
   * combined view, not a filter — it changes how rows are measured, never
   * which rows are returned.
   */
  callOffWarehouseBasis: boolean
  /**
   * CR-5454: Cost-tab-only filters (see costTabFilters.ts). Optional so the
   * many places that build a filter object need not know about them; cleared
   * on leaving the Cost tab.
   */
  /** '' (Both) | 'contracted' | 'not_contracted' */
  contractStatus?: string
  /** Free-text, case-insensitive "contains" part number search. */
  partNumber?: string
  /** Contract type NAME — types are per company, names are shared across a group. */
  contractType?: string
}

// ============================================================================
// Toast State
// ============================================================================

export interface ToastState {
  message: string
  type: 'success' | 'error'
  visible: boolean
}

// ============================================================================
// Tab Definition
// ============================================================================

export interface DashboardTab {
  id: string
  label: string
  active?: boolean
}

/**
 * Single source of truth for all dashboard tabs.
 * Used by Dashboard.tsx for rendering and by the Dashboard items in Navigation Menu Management (PlatformTab) for settings.
 * To add/remove a dashboard tab, update this array only.
 */
export const DASHBOARD_TAB_REGISTRY: Array<{
  id: string
  label: string
  description: string
}> = [
  { id: 'default', label: 'Default', description: 'Overview dashboard with key metrics, KPIs, and summary charts' },
  { id: 'scheduled', label: 'Scheduled Dashboards', description: 'Live firm-window vs forecast schedules — qty, value, and coverage by part schedule_firm days (scheduling-agreement orders only)' },
  { id: 'spot-orders', label: 'Spot Orders', description: 'Spot / one-off orders — same KPI lens as Scheduled Dashboards but scoped to ERP order type 1' },
  // PO-line-grain sibling of the Scheduled Dashboard for PO-line-grain customers
  // (effective per-customer delivery grain — MenuSettingsContext.usesScheduleLines).
  // Hidden automatically for schedule-grain customers.
  { id: 'po-lines', label: 'Current Performance', description: 'How delivery, quality and cost are tracking right now — this month, this week, a 12-week trend, year to date and the last 12 months. OTIF is judged per PO line, OTD per receipt.' },
  { id: 'cached-performance', label: 'Cached Performance', description: 'Point-in-time KPI snapshots captured nightly (frozen, will not shift as live data changes)' },
  { id: 'group-kpi', label: 'Group KPI', description: 'Group-level KPI analytics with internal/external performance' },
  { id: 'group-detail-kpi', label: 'Group Detail KPI', description: 'Detailed group-level KPI breakdown and drill-down data' },
  // CR-5453: rename "Company KPI" to "Site KPI" so the tab label matches
  // the underlying intent (per-site supplier-specific view, not company
  // roll-up). Section key unchanged so tour config + analytics events
  // keep firing on the same id.
  { id: 'site-kpi', label: 'Site KPI', description: 'Site-level OTIF, PPM, delivery depth, and spend analytics' },
  { id: 'cost', label: 'Cost', description: 'Incoming order value, arrears analysis, and spend tracking' },
  { id: 'emissions', label: 'Emissions', description: 'Carbon emissions tracking by transport mode and supplier' },
  { id: 'compliance', label: 'Compliance', description: 'Supplier approval status, risk categories, and questionnaire tracking' },
  { id: 'future-forecast', label: 'Future Forecast', description: 'Forward-looking delivery and demand forecasting' },
  { id: 'date-adherence', label: 'Date Adherence', description: 'Supplier delivery date adherence metrics and trends' },
  // METRIC-DEFINITIONS Q47 / W3-T20 R10: this tab buckets lines by DUE-DATE
  // PROXIMITY. Legacy's "Expediting" counts every ODD/CRD change in the
  // change log — activity a buyer took — and customers learned the word
  // there. A familiar name over an unfamiliar number is the worst outcome,
  // so the Helix metric is the one renamed. The route id is unchanged.
  { id: 'expediting', label: 'Upcoming & overdue', description: 'Lines by how close they are to their due date, and what is already overdue' },
  { id: 'performance-ratings', label: 'Performance Ratings', description: 'Supplier performance rating and scoring overview' },
  // CR-6310: Performance tab with grid and chart views for supplier performance metrics
  { id: 'performance', label: 'Supplier Performance', description: 'Supplier performance metrics grid with time-series data and chart visualization' },
]

// ============================================================================
// Commodity Types
// ============================================================================

export interface CommodityGroup {
  id: number
  name: string
  code?: string
  description?: string
  is_active: boolean
}

export interface CommodityType {
  id: number
  commodity_group: number
  commodity_group_name?: string
  name: string
  code?: string
  description?: string
  is_active: boolean
}

// ============================================================================
// Emissions Data Types
// ============================================================================

export interface EmissionsData {
  total_carbon_kg: number
  by_transport: {
    road: { emissions_kg: number; distance_km: number; percentage: number }
    rail: { emissions_kg: number; distance_km: number; percentage: number }
    air: { emissions_kg: number; distance_km: number; percentage: number }
    sea: { emissions_kg: number; distance_km: number; percentage: number }
  }
  by_month: Array<{
    month: string
    road: number
    rail: number
    air: number
    sea: number
    total: number
  }>
  by_supplier: Array<{
    supplier: string
    emissions_kg: number
    percentage: number
  }>
  trend: Array<{
    month: string
    emissions: number
    trend_line: number
  }>
}

// ============================================================================
// Site KPI Data Types (Reference Design)
// ============================================================================

export interface SiteKPIData {
  otif: {
    month: number
    three_month: number
    twelve_month: number
  }
  appeals: {
    raised_by_customer: number
    raised_by_supplier: number
    accepted: number
    rejected: number
    auto_approved: number
  }
  spend: {
    month: number
    three_month: number
    twelve_month: number
  }
  otif_trend: Array<{
    month: string
    otif: number
    target: number
  }>
  appeals_trend: Array<{
    month: string
    awaiting_customer: number
    accepted: number
    awaiting_supplier: number
    rejected: number
  }>
  spend_trend: Array<{
    month: string
    spend: number
  }>
  depth_of_delivery: Array<{
    month: string
    on_time: number
    early: number
    late: number
  }>
  arrears_by_lines: Array<{
    month: string
    lines: number
  }>
  arrears_by_value: Array<{
    month: string
    value: number
  }>
}

// ============================================================================
// Compliance Data Types
// ============================================================================

export interface ComplianceData {
  live_contracts: number
  docs_awaiting_read: number
  suppliers_by_approval: Array<{
    status: string
    count: number
    percentage: number
    color: string
  }>
  suppliers_by_risk: Array<{
    category: string
    count: number
    percentage: number
    color: string
  }>
  questionnaires_by_status: Array<{
    status: string
    count: number
    percentage: number
    color: string
  }>
  contracts_by_auto_renew: Array<{
    type: string
    count: number
    percentage: number
    color: string
  }>
}
